import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SITE_URL } from "@/lib/api";
import {
  fetchBlogDrafts, publishBlogDraft, unpublishBlogDraft,
  fetchBlogBackfillStatus,
} from "@/lib/dashboardApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  FileClock, Loader2, CheckCircle2, AlertTriangle, Eye, Rocket, Undo2, ExternalLink, Sparkles,
} from "lucide-react";

export default function DraftReview() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["blog-drafts"], queryFn: fetchBlogDrafts });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [flaggedFirst, setFlaggedFirst] = useState(true);
  const [backfill, setBackfill] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const s = await fetchBlogBackfillStatus();
      setBackfill(s);
      if (s?.state?.running) {
        if (!pollRef.current) pollRef.current = setInterval(async () => { await loadStatus(); refetch(); }, 10000);
      } else if (pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null; refetch();
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadStatus(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const doPublish = async (d) => {
    setBusy(d.slug);
    try {
      await publishBlogDraft(d.slug);
      toast.success(`Published "${d.title}" — it's now live and in the sitemap.`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish."); }
    setBusy(null);
  };
  const doUnpublish = async (d) => {
    setBusy(d.slug);
    try {
      await unpublishBlogDraft(d.slug);
      toast.success(`"${d.title}" moved back to draft.`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not unpublish."); }
    setBusy(null);
  };

  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const drafts = data.drafts || [];
  const gap30 = drafts.filter((d) => d.source === "gap30");
  const displayed = flaggedFirst
    ? [...drafts].sort((a, b) => (a.qc_passed ? 1 : 0) - (b.qc_passed ? 1 : 0))
    : drafts;
  const bf = backfill?.state;
  const bfRunning = !!bf?.running;

  return (
    <div data-testid="draft-review-root">
      <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><FileClock className="h-6 w-6 text-launch" /> Draft Review</h1>
      <p className="text-slate-500 mt-1 mb-6">Review every AI-written content-gap article and publish the ones you approve. Publishing makes a post live immediately and adds it to the sitemap.</p>

      {bfRunning && (
        <div className="rounded-xl border border-launch/30 bg-launch/5 p-4 mb-6 flex items-center gap-3" data-testid="draft-generation-banner">
          <Sparkles className="h-5 w-5 text-launch animate-pulse" />
          <div className="text-sm">
            <p className="font-semibold text-navy">Generating content-gap drafts… {bf.done || 0}/{bf.requested || 0} written</p>
            <p className="text-xs text-slate-500">You can leave this page — drafts appear here as they finish. New drafts: {bf.drafted || 0} · Failed: {bf.failed || 0}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-6">
        <Stat label="Drafts awaiting review" value={drafts.length} testid="stat-total-drafts" />
        <Stat label="Content-gap batch" value={gap30.length} testid="stat-gap30-drafts" />
        <Stat label="QC passed" value={drafts.filter((d) => d.qc_passed).length} testid="stat-qc-passed" />
        <Stat label="Flagged for review" value={drafts.filter((d) => !d.qc_passed).length} testid="stat-qc-flagged" />
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">{displayed.length} draft{displayed.length === 1 ? "" : "s"} to review</p>
        <button onClick={() => setFlaggedFirst((v) => !v)} data-testid="flagged-first-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${flaggedFirst ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Flagged first {flaggedFirst ? "· on" : "· off"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="drafts-table">
        {drafts.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
            No drafts awaiting review. {bfRunning ? "Generation in progress…" : "All caught up!"}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Article</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Category</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Words</th>
                <th className="px-4 py-3 text-left">QC</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((d) => (
                <tr key={d.slug} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`draft-row-${d.slug}`}>
                  <td className="px-4 py-3 max-w-md">
                    <p className="font-semibold text-navy truncate">{d.title}</p>
                    <p className="text-xs text-slate-400 truncate">{d.primary_keyword ? `kw: ${d.primary_keyword} · ` : ""}/blog/{d.slug}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">{d.category || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600">{d.word_count}</td>
                  <td className="px-4 py-3">
                    {d.qc_passed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Passed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" title={(d.qc_flags || []).join(", ")}><AlertTriangle className="h-3.5 w-3.5" /> {(d.qc_flags || []).length || "Review"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setPreview(d)} data-testid={`draft-preview-${d.slug}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100">
                        <Eye className="h-3.5 w-3.5" /> Preview
                      </button>
                      <button onClick={() => doPublish(d)} disabled={busy === d.slug} data-testid={`draft-publish-${d.slug}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-launch hover:bg-launch/90 disabled:opacity-50">
                        {busy === d.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />} Publish
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="draft-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="text-navy pr-8">{preview.title}</DialogTitle>
                <DialogDescription className="text-xs text-slate-400">Draft preview — review the full article before publishing.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 border-b border-slate-100 pb-3">
                <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold">{preview.category || "General"}</span>
                <span>{preview.word_count} words</span>
                {preview.qc_passed ? <span className="text-emerald-600 font-semibold">QC passed</span>
                  : <span className="text-amber-600 font-semibold">Flags: {(preview.qc_flags || []).join(", ") || "review"}</span>}
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p><span className="font-semibold text-slate-700">Title tag:</span> {preview.seo_title}</p>
                <p><span className="font-semibold text-slate-700">Meta:</span> {preview.meta_description || "—"}</p>
                {preview.internal_links?.length > 0 && (
                  <p><span className="font-semibold text-slate-700">Internal links:</span> {preview.internal_links.map((l) => l.url || l).join(" · ")}</p>
                )}
              </div>
              {preview.featured_image && (
                <img src={preview.featured_image} alt={preview.title} className="rounded-lg w-full object-cover max-h-56" />
              )}
              <article className="prose prose-sm max-w-none prose-headings:text-navy prose-a:text-launch mt-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.content || ""}</ReactMarkdown>
              </article>
              <div className="sticky bottom-0 bg-white border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
                <a href={`${SITE_URL}/blog/${preview.slug}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy">
                  <ExternalLink className="h-3.5 w-3.5" /> Open preview URL
                </a>
                <div className="flex gap-2">
                  <button onClick={() => { doUnpublish(preview); }} data-testid="draft-dialog-reject"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100">
                    <Undo2 className="h-3.5 w-3.5" /> Keep as draft
                  </button>
                  <button onClick={() => { doPublish(preview); setPreview(null); }} data-testid="draft-dialog-publish"
                    className="inline-flex items-center gap-1 px-4 py-1.5 rounded-md text-xs font-bold text-white bg-launch hover:bg-launch/90">
                    <Rocket className="h-3.5 w-3.5" /> Approve & Publish
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 min-w-[140px]" data-testid={testid}>
      <p className="text-2xl font-bold text-navy">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
